import {
  ref,
  computed,
  watch,
  onMounted,
  getCurrentInstance,
  type Ref,
} from 'vue';
import { useStore } from 'vuex';
import {
  TCity,
  TFilterCriteria,
  TUser,
} from 'Services/types/counselor-grid-service-types';
import { filterAndSortUsersByCriteria } from 'Services/counselor-grid-filter-service';
import { ESortByOptionValue } from 'Services/types/counselor-grid-service-sort-option-types';

export function useCounselorGridFilter(preparedUsersGetter: () => TUser[]) {
  const store = useStore();

  const latestSearchedForCities: Ref<TCity[]> = ref([]);
  const matchingUsers: Ref<TUser[]> = ref([]);
  const partiallyMatchingUsers: Ref<TUser[]> = ref([]);
  const notMatchingUsers: Ref<TUser[]> = ref([]);
  const currentSite = ref(window.theme_vars?.currentSite || '');
  // $root.$data.role is seeded from the .counselor-grid__app data-role
  // attribute (see counselor-grid/index.js).
  const instance = getCurrentInstance();
  const proxy = instance?.proxy as
    | { roleProp?: string; $root?: { $data?: { role?: string } } }
    | undefined;
  const role = ref<string>(
    proxy?.roleProp || proxy?.$root?.$data?.role || ''
  );
  const shouldLogFilters = ref(false);
  const loggedFilters: Ref<Record<string, unknown>> = ref({});

  // Computed from store
  const cities = computed(() => store.state.cities.cities);
  const filteredAccreditations = computed(
    () => store.state.counselorGrid.filteredAccreditations
  );
  const filteredCompetenceAreas = computed(
    () => store.state.counselorGrid.filteredCompetenceAreas
  );
  const filteredGender = computed(
    () => store.state.counselorGrid.filteredGender
  );
  const filteredLanguages = computed(
    () => store.state.counselorGrid.filteredLanguages
  );
  const filteredLocation = computed(
    () => store.state.counselorGrid.filteredLocation
  );
  const filteredPlace = computed(() => store.state.counselorGrid.filteredPlace);
  const filteredMeetingTypes = computed(
    () => store.state.counselorGrid.filteredMeetingTypes
  );
  const filteredTargetGroups = computed(
    () => store.state.counselorGrid.filteredTargetGroups
  );
  const filteredTitles = computed(
    () => store.state.counselorGrid.filteredTitles
  );
  const filteredYearsOfExperience = computed(
    () => store.state.counselorGrid.filteredYearsOfExperience
  );
  const filteredAvailableTimes = computed(
    () => store.state.counselorGrid.filteredAvailableTimes
  );
  const filteredAllowedDistance = computed(
    () => store.state.counselorGrid.filteredAllowedDistance
  );
  const sortBy = computed(() => store.state.counselorGrid.sortBy);
  const googleMapsIsAvailable = computed(
    () => store.state.counselorGrid.googleMapsIsAvailable
  );

  function setIsFiltering(val: boolean) {
    store.commit('counselorGrid/setIsFiltering', val);
  }

  function setSortBy(val: string) {
    store.commit('counselorGrid/setSortBy', val);
  }

  async function filterUsers() {
    setIsFiltering(true);

    const filters: TFilterCriteria = {
      competenceAreas: filteredCompetenceAreas.value,
      accreditations: filteredAccreditations.value,
      targetGroups: filteredTargetGroups.value,
      languages: filteredLanguages.value,
      meetingTypes: filteredMeetingTypes.value,
      location: filteredLocation.value,
      place: filteredPlace.value,
      allowedDistance: filteredAllowedDistance.value,
      titles: filteredTitles.value,
      gender: filteredGender.value,
      yearsOfExperience: filteredYearsOfExperience.value,
      availableTimes: filteredAvailableTimes.value,
    };

    try {
      const result = await filterAndSortUsersByCriteria(
        preparedUsersGetter(),
        filters,
        role.value || '',
        latestSearchedForCities.value,
        sortBy.value || ESortByOptionValue.Default,
        googleMapsIsAvailable.value,
        currentSite.value || ''
      );

      matchingUsers.value = result.matchingUsers;
      partiallyMatchingUsers.value = result.partiallyMatchingUsers;
      notMatchingUsers.value = result.notMatchingUsers;

      logFilterChange(filters);
    } catch (error) {
      console.error('Error filtering users', error);
    }

    setIsFiltering(false);
    updateUrlWithFilters(filters);
  }

  function logFilterChange(filters: TFilterCriteria) {
    if (!shouldLogFilters.value) {
      return;
    }

    if (
      Object.keys(loggedFilters.value).length > 0 &&
      JSON.stringify(loggedFilters.value) === JSON.stringify(filters)
    ) {
      return;
    }

    (window as any).dataLayer = (window as any).dataLayer || [];

    const dataLayerObject: { [key: string]: string | number | null } = {
      event: 'filter_applied',
      eventCategory: 'Arkivvy',
      eventAction: 'Ändrat filter',
      prompt: store.getters['users/getSearchedPrompt'],
    };

    for (let [key, value] of Object.entries(filters)) {
      if (Array.isArray(value)) {
        value = value.join(', ');
      }

      if (typeof value === 'object') {
        value = JSON.stringify(value);
      }

      dataLayerObject[`filter_${key}`] = value as string | number;
    }

    dataLayerObject['matching_users_count'] = matchingUsers.value.length;
    dataLayerObject['partially_matching_user_count'] =
      partiallyMatchingUsers.value.length;
    dataLayerObject['not_matching_users_count'] = notMatchingUsers.value.length;

    (window as any).dataLayer.push(dataLayerObject);
    loggedFilters.value = filters;
  }

  function updateUrlWithFilters(filters: TFilterCriteria) {
    const isValidValue = (v: unknown) =>
      (typeof v === 'number' && v !== 0) ||
      (typeof v === 'string' && v !== '') ||
      (typeof v === 'boolean' && v != null);

    const paramsArr = Object.entries(filters)
      .filter(([key, value]) => {
        if (key === 'place') return false;
        if (Array.isArray(value)) {
          return value.some(isValidValue);
        }
        return isValidValue(value);
      })
      .map(([key, value]) => {
        if (Array.isArray(value)) {
          const filtered = value.filter(isValidValue);
          return filtered.length
            ? `${encodeURIComponent(key)}=${encodeURIComponent(filtered.join(','))}`
            : '';
        }
        return `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`;
      })
      .filter(Boolean);

    if (
      filters.place &&
      typeof filters.place === 'object' &&
      (filters.place as any).name
    ) {
      paramsArr.push(
        `therapyLocation=${encodeURIComponent((filters.place as any).name)}`
      );
    }

    const params = paramsArr.join('&');
    const newUrl = params
      ? `${window.location.pathname}?${params}`
      : window.location.pathname;
    window.history.replaceState({}, '', newUrl);
  }

  async function applyFiltersFromUrl() {
    const params = new URLSearchParams(window.location.search);

    const filterConfig = [
      {
        key: 'accreditations',
        type: 'array',
        stateKey: 'filteredAccreditations',
      },
      {
        key: 'allowedDistance',
        type: 'number',
        stateKey: 'filteredAllowedDistance',
      },
      {
        key: 'availableTimes',
        type: 'array',
        stateKey: 'filteredAvailableTimes',
      },
      {
        key: 'competenceAreas',
        type: 'array',
        stateKey: 'filteredCompetenceAreas',
      },
      { key: 'gender', type: 'string', stateKey: 'filteredGender' },
      { key: 'languages', type: 'array', stateKey: 'filteredLanguages' },
      { key: 'location', type: 'string', stateKey: 'filteredLocation' },
      { key: 'meetingTypes', type: 'array', stateKey: 'filteredMeetingTypes' },
      { key: 'targetGroups', type: 'array', stateKey: 'filteredTargetGroups' },
      {
        key: 'therapyLocation',
        type: 'therapyLocation',
        stateKey: 'filteredPlace',
      },
      { key: 'titles', type: 'array', stateKey: 'filteredTitles' },
      {
        key: 'yearsOfExperience',
        type: 'number',
        stateKey: 'filteredYearsOfExperience',
      },
    ];

    const filtersToApply: Record<
      string,
      string | number | string[] | { name: string } | null
    > = {};

    filterConfig.forEach(({ key, type, stateKey }) => {
      if (!params.has(key)) return;
      const value = params.get(key);

      switch (type) {
        case 'array':
          filtersToApply[stateKey] = value
            ? value.split(',').filter((v) => v !== '' && v != null)
            : [];
          break;
        case 'number':
          filtersToApply[stateKey] = value !== null ? Number(value) : 0;
          break;
        case 'string':
          filtersToApply[stateKey] = value || '';
          break;
        case 'therapyLocation':
          filtersToApply[stateKey] = value ? { name: value } : null;
          break;
      }
    });

    // Seed latestSearchedForCities synchronously before filterUsers() runs;
    // the cities watcher would otherwise race and the parent-city fallback
    // in filterUsersByLocation would see an empty list.
    if (
      typeof filtersToApply.filteredLocation === 'string' &&
      filtersToApply.filteredLocation
    ) {
      const preloadedCities = (await store.dispatch('cities/getCities', {
        search: filtersToApply.filteredLocation,
      })) as TCity[] | undefined;

      if (Array.isArray(preloadedCities) && preloadedCities.length > 0) {
        latestSearchedForCities.value = preloadedCities;
      }
    }

    store.commit('counselorGrid/setAllFilters', filtersToApply);
    filterUsers();
  }

  // Watchers
  watch(filteredCompetenceAreas, () => filterUsers());
  watch(filteredAccreditations, () => filterUsers());
  watch(filteredTargetGroups, () => filterUsers());
  watch(filteredLanguages, () => filterUsers());
  watch(filteredMeetingTypes, () => filterUsers());
  watch(filteredTitles, () => filterUsers());
  watch(filteredGender, () => filterUsers());
  watch(filteredYearsOfExperience, () => filterUsers());
  watch(filteredAvailableTimes, () => filterUsers());
  watch(filteredLocation, () => filterUsers());
  watch(filteredPlace, () => filterUsers());
  watch(filteredAllowedDistance, () => filterUsers());
  watch(sortBy, () => filterUsers());

  watch(cities, (newCities: TCity[]) => {
    if (newCities.length > 0) {
      latestSearchedForCities.value = newCities;
    }
  });

  onMounted(() => {
    applyFiltersFromUrl();
  });

  return {
    latestSearchedForCities,
    matchingUsers,
    partiallyMatchingUsers,
    notMatchingUsers,
    currentSite,
    role,
    shouldLogFilters,
    loggedFilters,
    cities,
    filteredAccreditations,
    filteredCompetenceAreas,
    filteredGender,
    filteredLanguages,
    filteredLocation,
    filteredPlace,
    filteredMeetingTypes,
    filteredTargetGroups,
    filteredTitles,
    filteredYearsOfExperience,
    filteredAvailableTimes,
    filteredAllowedDistance,
    sortBy,
    googleMapsIsAvailable,
    setIsFiltering,
    setSortBy,
    filterUsers,
    logFilterChange,
    updateUrlWithFilters,
    applyFiltersFromUrl,
  };
}
